import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";

import { Provider, getServerData, getTemplate } from "@reactivated";

const { props, context } = getServerData();

if (context.SENTRY_DSN) {
    Sentry.init({
        dsn: context.SENTRY_DSN,
        release: context.CI_COMMIT_SHA,
        environment: context.CI_COMMIT_REF_SLUG,
        integrations: [],
        ignoreErrors: [
            // Safari/WebKit IndexedDB connection loss — browser-internal error with no first-party frames
            // https://thelab.sentry.io/issues/TSI-TEMPURPEDIC-Z9D
            /Connection to Indexed Database server lost/,
            // Filter out client-side noise from iOS in-app browsers (Instagram, Facebook)
            // trying to call native WebKit message handlers that don't exist.
            // https://thelab.sentry.io/issues/TSI-TEMPURPEDIC-1771
            /window\.webkit\.messageHandlers/,
            // Module import failures in Instagram in-app browser and iOS Safari —
            // browser-level TypeError with no stacktrace or first-party frames.
            // https://thelab.sentry.io/issues/TSI-SEALY-43X
            /Importing a module script failed/,
            // Wistia video player MediaError — browser-native <video> playback failures (HLS decode,
            // network interruption, unsupported codec). No stacktrace or first-party frames.
            // https://thelab.sentry.io/issues/TSI-TEMPURPEDIC-12YP
            /\[object MediaError\]/,
            // iOS WebKit JS parsing error from third-party scripts — no first-party frames,
            // exclusively iOS browsers (Firefox iOS, Ecosia iOS). Global onerror handler.
            // https://thelab.sentry.io/issues/TSI-TEMPURPEDIC-ZD3
            /No identifiers allowed directly after numeric literal/,
            // Generic browser fetch() network failure from third-party scripts — no first-party
            // frames or source maps, only minified anonymous stacks. Some browsers append the
            // failing domain (e.g. "Failed to fetch (w88p9x.com)"), so we anchor only the start.
            // https://thelab.sentry.io/issues/TSI-TEMPURSEALY2-91
            // https://thelab.sentry.io/issues/TSI-TEMPURSEALY2-92
            /^Failed to fetch/,
        ],
        // Filter out errors from third-party scripts we don't control
        denyUrls: [
            // OneTrust cookie consent banner SDK
            /otBannerSdk\.js/i,
            // Wistia video player external plugins (airplay, etc.) —
            // internal Wistia errors with no first-party frames.
            // https://thelab.sentry.io/issues/TSI-TEMPURPEDIC-15QY
            /\/assets\/external\/airplay\.js/i,
        ],
    });
}

const main = async () => {
    const Template = await getTemplate(context);

    ReactDOM.hydrateRoot(
        document,
        <Provider value={context}>
            <Template {...props} />
        </Provider>,
    );
};

await main();
